// The hover box's rows. Values stay the LR SDK's (FeatureInfoControl.getKeyValuesAt,
// whose output the hover styles also read); this groups them by key prefix and adds
// the section or point pair each comes from.
import { buildProfile, profileValueAt, stepValueAt } from "./positions.mjs";
import { isCurvatureKey } from "./renderValues.mjs";
import {
  MAX_SPEED_LABEL,
  NDS_CURVATURE_LABEL,
  RADIUS_LABEL,
} from "../layers/style/util/curvatureSpeed.mjs";

const CURVATURE_DERIVED = new Set([
  NDS_CURVATURE_LABEL,
  RADIUS_LABEL,
  MAX_SPEED_LABEL,
]);

/** FCD direction ids count from 1 = north, 30° apart. */
export const fcdHeadingDeg = (directionId) => (directionId - 1) * 30;

function rangeAt(tag, lengthCm, cm) {
  if (!tag.sections || tag.lr === "spot") return { rangeText: "" };
  if (tag.lr === "linear") {
    const at = profileValueAt(buildProfile(tag.sections, lengthCm), cm);
    if (!at) return { rangeText: "" };
    // Only a non-numeric band (`multiple`) replaces the SDK's value; a numeric
    // ranged section is a value like any other.
    const band =
      typeof at.value === "string" && !Number.isFinite(Number(at.value));
    const rangeText =
      at.startCm === at.endCm
        ? `at ${at.startCm}`
        : `${at.interpolated ? "between " : ""}${at.startCm}–${at.endCm}`;
    return { rangeText, band: band ? at.value : undefined };
  }
  const at = stepValueAt(tag.sections, lengthCm, cm);
  return { rangeText: at ? `${at.startCm}–${at.endCm}` : "" };
}

/**
 * @param {import("./featureModel.mjs").FeatureModel} model
 * @param {number} cm position along the feature
 * @param {[string, unknown][]} keyValuesAt from FeatureInfoControl.getKeyValuesAt
 */
export function hoverView(model, cm, keyValuesAt) {
  const tagByRawKey = new Map(
    model.groups.flatMap((g) =>
      g.tags.map((t) => [t.rawKey, { tag: t, group: g }]),
    ),
  );
  const rowsByGroup = new Map();
  const derived = [];
  let inBand = false;
  keyValuesAt.forEach(([key, value]) => {
    const hit = tagByRawKey.get(key);
    if (!hit) {
      derived.push([key, value]);
      return;
    }
    const { rangeText, band } = rangeAt(hit.tag, model.lengthCm, cm);
    if (band !== undefined && isCurvatureKey(hit.tag.key)) inBand = true;
    if (!rowsByGroup.has(hit.group)) rowsByGroup.set(hit.group, []);
    rowsByGroup.get(hit.group).push({
      key: hit.tag.key,
      value: band ?? value,
      rangeText,
    });
  });
  return {
    id: model.id,
    positionText:
      model.lengthCm === null
        ? `position ${cm} cm`
        : `position ${cm} / ${model.lengthCm} cm`,
    groups: model.groups
      .filter((g) => rowsByGroup.has(g))
      .map((g) => ({ prefix: g.prefix, rows: rowsByGroup.get(g) })),
    // Inside a curvature `multiple` stretch there is no curvature to derive from.
    derived: inBand
      ? derived.filter(([key]) => !CURVATURE_DERIVED.has(key))
      : derived,
  };
}
